import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Pill, Trash2, Plus, PencilLine } from "lucide-react";

const CHIP = {
  covered: "bg-[#DCFCE7] text-[#166534] border-[#86EFAC]",
  not_covered: "bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]",
  no_drug_coverage: "bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]",
  needs_verification: "bg-[#F3F4F6] text-[#374151] border-[#D1D5DB]",
};
const REFILLS = [30, 60, 90];

function MedSearch({ base, onPick, onCancel }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (q.trim().length < 3) { setRes(null); return undefined; }
    let live = true;
    const t = setTimeout(async () => {
      setBusy(true);
      try { const { data } = await api.get(`${base}/search`, { params: { q: q.trim() } }); if (live) setRes(data); }
      catch { if (live) setRes({ ok: false }); } finally { if (live) setBusy(false); }
    }, 600);
    return () => { live = false; clearTimeout(t); };
  }, [q, base]);
  return (
    <div className="mt-3 rounded-xl border border-[#E5E2DA] bg-white p-3" data-testid="mymed-search">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Medication name (e.g. lisinopril)" className="h-12 text-base" data-testid="mymed-search-input" />
      {busy && <p className="mt-2 text-sm text-[#5B6670]" data-testid="mymed-search-loading"><Loader2 className="w-4 h-4 inline animate-spin mr-1" />Searching…</p>}
      {res && res.ok === false && <p className="mt-2 text-sm text-[#5B6670]" data-testid="mymed-search-error">Medication search isn't available right now. Please try again shortly.</p>}
      {res?.suggestion && <p className="mt-2 text-xs text-[#5B6670]" data-testid="mymed-suggestion">Showing results for "{res.suggestion}".</p>}
      {res?.ok && (
        <ul className="mt-2 space-y-1.5 max-h-72 overflow-y-auto" data-testid="mymed-results">
          {res.results.length === 0 && <li className="text-sm text-[#5B6670]" data-testid="mymed-no-results">No exact strength and form found. Check the spelling on your prescription label.</li>}
          {res.results.map((r) => (
            <li key={r.rxcui}>
              <button type="button" onClick={() => onPick(r)} className="w-full text-left rounded-lg border border-[#E5E2DA] p-2.5 hover:border-[#1E3E62]" data-testid={`mymed-option-${r.rxcui}`}>
                <div className="font-semibold text-[#0B192C]">{r.strength_form}</div>
                <div className="text-xs text-[#5B6670]">{r.brand ? `Brand: ${r.brand}` : "Generic"}</div>
              </button>
            </li>
          ))}
        </ul>
      )}
      <button type="button" onClick={onCancel} className="mt-2 text-sm underline text-[#1E3E62]" data-testid="mymed-search-cancel">Cancel</button>
    </div>
  );
}

function MedForm({ med, onSave, onCancel }) {
  const p = med.pharmacy || {};
  const [f, setF] = useState({ quantity: med.quantity || 30, refill_days: med.refill_days || 30, kind: p.kind || "retail", name: p.name || "", zip: p.zip || "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = () => onSave({ rxcui: med.rxcui, name: med.name, tty: med.tty, brand: med.brand, strength_form: med.strength_form,
    quantity: Number(f.quantity), refill_days: Number(f.refill_days), pharmacy: { kind: f.kind, name: f.name.trim() || null, zip: f.zip.trim() || null } });
  return (
    <div className="mt-3 rounded-xl border border-[#E5E2DA] bg-white p-3 grid gap-2" data-testid="mymed-form">
      <div className="font-semibold text-[#0B192C]" data-testid="mymed-form-name">{med.strength_form || med.name}</div>
      <div className="flex flex-wrap gap-2 items-center">
        <label className="text-sm text-[#5B6670]">Quantity<Input type="number" min="1" value={f.quantity} onChange={set("quantity")} className="h-12 w-28 text-base" data-testid="mymed-quantity" /></label>
        <label className="text-sm text-[#5B6670]">Refill every
          <select value={f.refill_days} onChange={set("refill_days")} className="block h-12 rounded-md border px-2 text-base" data-testid="mymed-refill">
            {REFILLS.map((d) => <option key={d} value={d}>{d} days</option>)}
          </select>
        </label>
        <label className="text-sm text-[#5B6670]">Pharmacy
          <select value={f.kind} onChange={set("kind")} className="block h-12 rounded-md border px-2 text-base" data-testid="mymed-pharmacy-kind">
            <option value="retail">Retail pharmacy</option><option value="mail">Mail order</option>
          </select>
        </label>
      </div>
      {f.kind === "retail" && (
        <div className="flex flex-wrap gap-2">
          <Input value={f.name} onChange={set("name")} placeholder="Preferred pharmacy (optional)" className="h-12 flex-1 min-w-[180px] text-base" data-testid="mymed-pharmacy-name" />
          <Input value={f.zip} onChange={set("zip")} placeholder="Pharmacy ZIP" className="h-12 w-32 text-base" data-testid="mymed-pharmacy-zip" />
        </div>
      )}
      <p className="text-xs text-[#5B6670]">We save these details for cost checks later. We don't estimate drug costs.</p>
      <div className="flex gap-2">
        <Button type="button" disabled={!(Number(f.quantity) > 0)} onClick={submit} className="h-11 rounded-full bg-[#0B192C] text-white" data-testid="mymed-save">Save medication</Button>
        <Button type="button" variant="outline" onClick={onCancel} className="h-11 rounded-full" data-testid="mymed-form-cancel">Cancel</Button>
      </div>
    </div>
  );
}

const flags = (r) => [r.prior_auth && "Prior authorization", r.step_therapy && "Step therapy", r.quantity_limit && (r.quantity_limit_text ? `Quantity limit: ${r.quantity_limit_text}` : "Quantity limit")].filter(Boolean);

function MedCell({ p, r }) {
  const f = flags(r);
  return (
    <li className="text-sm" data-testid={`mymed-cell-${p.plan}-${r.med_key}`}>
      <span className={`inline-block rounded-full border px-2 py-0.5 text-xs font-semibold mr-1.5 ${CHIP[r.status] || CHIP.needs_verification}`} data-testid={`mymed-status-${p.plan}-${r.med_key}`}>{r.label}</span>
      {r.name}
      {r.status === "covered" && <span className="ml-1 text-[11px] text-[#374151]" data-testid={`mymed-restrictions-${p.plan}-${r.med_key}`}>{f.length ? `· ${f.join(" · ")}` : "· No restrictions listed"}</span>}
      {r.stale && <span className="ml-1 text-[11px] font-semibold text-[#B45309]" data-testid={`mymed-stale-${p.plan}-${r.med_key}`}>Check expired — refreshing</span>}
      {r.status !== "covered" && r.reason && <div className="text-[11px] text-[#6B7280] ml-1">{r.reason}</div>}
    </li>
  );
}

function MedMatrix({ matrix, onRequestAgent }) {
  return (
    <div className="mt-4" data-testid="mymed-matrix">
      <h4 className="font-semibold text-[#0B192C]">Your medications by plan ({matrix.year})</h4>
      <p className="text-xs text-[#5B6670]">"Needs verification" means we couldn't confirm it yet. It doesn't mean the drug isn't covered. We don't estimate costs.</p>
      <ul className="mt-2 space-y-2">
        {matrix.plans.map((p) => (
          <li key={p.plan} className="rounded-xl border border-[#E5E2DA] bg-white p-3" data-testid={`mymed-plan-${p.plan}`}>
            <div className="font-semibold text-sm text-[#0B192C]">{p.plan_name || p.plan} <span className="font-normal text-[#8A8F98]">· {p.plan}</span></div>
            <div className="mt-1 text-xs font-semibold text-[#166534]" data-testid={`mymed-covered-${p.plan}`}>Confirmed covered: {p.covered} of {p.meds.length}</div>
            <ul className="mt-1 space-y-1">{p.meds.map((r) => <MedCell key={r.med_key} p={p} r={r} />)}</ul>
          </li>
        ))}
      </ul>
      {onRequestAgent && <button type="button" onClick={onRequestAgent} className="mt-2 text-sm underline text-[#1E3E62]" data-testid="mymed-ask-agent-btn">Ask a licensed insurance agent to confirm these medications</button>}
    </div>
  );
}

export function MyMedications({ plans, token, isMember, onRequestAgent, baseOverride }) {
  const base = baseOverride || (isMember ? "/consumer/my-meds" : `/public/ask/${token}/my-meds`);
  const [meds, setMeds] = useState([]);
  const [matrix, setMatrix] = useState(null);
  const [mode, setMode] = useState(null);
  const [editing, setEditing] = useState(null);
  const planKey = plans.map((p) => `${p.contract_id}-${p.pbp}-${p.segment_id}`).join(",");

  const refresh = useCallback(async () => {
    try { const { data } = await api.post(`${base}/status`, { plans }); setMeds(data.medications || []); setMatrix(data.matrix); }
    catch { toast.error("Couldn't check your medications right now."); }
  }, [base, planKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [refresh]);

  const save = async (m) => {
    try { await api.post(base, m); setMode(null); setEditing(null); toast.success(isMember ? "Medication saved to your household." : "Medication saved to this conversation."); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save the medication."); }
  };
  const remove = async (m) => {
    try { await api.delete(`${base}/${encodeURIComponent(m.med_key)}`); toast.success("Medication removed."); refresh(); }
    catch { toast.error("Couldn't remove the medication."); }
  };
  return (
    <section className="mt-5 rounded-2xl border border-[#E5E2DA] bg-[#FAF8F3] p-4" data-testid="my-medications">
      <h3 className="font-semibold text-[#0B192C] flex items-center gap-2"><Pill className="w-5 h-5" /> Your medications</h3>
      <p className="text-xs text-[#5B6670] mt-1">Add each medication with its exact strength and form. We check it against every plan's 2027 drug list.</p>
      <ul className="mt-3 space-y-2">
        {meds.map((m) => (
          <li key={m.med_key} className="rounded-xl border border-[#E5E2DA] bg-white p-3" data-testid={`mymed-saved-${m.med_key}`}>
            <div className="font-semibold text-[#0B192C]">{m.strength_form || m.name}{m.brand ? <span className="font-normal text-xs text-[#8A8F98]"> · {m.brand}</span> : null}</div>
            <div className="text-sm text-[#5B6670]" data-testid={`mymed-details-${m.med_key}`}>Qty {m.quantity} every {m.refill_days} days · {m.pharmacy?.kind === "mail" ? "Mail order" : (m.pharmacy?.name || "Retail pharmacy")}{m.pharmacy?.zip ? ` (${m.pharmacy.zip})` : ""}</div>
            {m.entered_by?.source === "agent" && <div className="text-[11px] text-[#92400E]" data-testid={`mymed-agent-${m.med_key}`}>Updated by agent {m.entered_by.name || ""} · {(m.entered_by.at || "").slice(0, 10)}</div>}
            <div className="mt-2 flex gap-3 text-sm">
              <button type="button" onClick={() => { setEditing(m); setMode("form"); }} className="underline text-[#1E3E62]" data-testid={`mymed-edit-${m.med_key}`}><PencilLine className="w-3.5 h-3.5 inline mr-0.5" />Edit</button>
              <button type="button" onClick={() => remove(m)} className="underline text-[#991B1B]" data-testid={`mymed-remove-${m.med_key}`}><Trash2 className="w-3.5 h-3.5 inline mr-0.5" />Remove</button>
            </div>
          </li>
        ))}
      </ul>
      {!mode && <Button type="button" onClick={() => setMode("search")} className="mt-3 h-11 rounded-full bg-[#0B192C] text-white" data-testid="mymed-add-btn"><Plus className="w-4 h-4 mr-1" />Add a medication</Button>}
      {mode === "search" && <MedSearch base={base} onPick={(r) => { setEditing(r); setMode("form"); }} onCancel={() => setMode(null)} />}
      {mode === "form" && editing && <MedForm med={editing} onSave={save} onCancel={() => { setMode(null); setEditing(null); }} />}
      {matrix && meds.length > 0 && <MedMatrix matrix={matrix} onRequestAgent={onRequestAgent} />}
    </section>
  );
}
